import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface SpeciesRecord {
  id: string;
  orgId: string;
  dtm: string;
  count: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  locationNote: string | null;
}

interface SpeciesRecordsListProps {
  orgId: string;
  speciesId: string;
}

export function SpeciesRecordsList({ orgId, speciesId }: SpeciesRecordsListProps) {
  const { data: records, isLoading, error } = useQuery({
    queryKey: ['orgSpeciesRecords', orgId, speciesId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/species/${speciesId}/records`);
      if (!res.ok) throw new Error('Failed to fetch records');
      return res.json() as Promise<SpeciesRecord[]>;
    },
    enabled: !!orgId && !!speciesId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Records</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            Failed to load records.
          </div>
        ) : records && records.length > 0 ? (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium">Count</th>
                    <th className="pb-2 font-medium">Location</th>
                    <th className="pb-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/app/org/${orgId}/record/${record.id}`}
                          className="text-primary hover:underline"
                        >
                          {new Date(record.dtm).toLocaleDateString()}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">
                        {record.count ?? '-'}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground max-w-[200px] truncate">
                        {record.locationNote ?? '-'}
                      </td>
                      <td className="py-2 text-muted-foreground max-w-[200px] truncate">
                        {record.note ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-2">
              {records.map((record) => (
                <Link
                  key={record.id}
                  to={`/app/org/${orgId}/record/${record.id}`}
                  className="block border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {new Date(record.dtm).toLocaleDateString()}
                    </span>
                    {typeof record.count === 'number' && (
                      <span className="text-sm text-muted-foreground">
                        x{record.count}
                      </span>
                    )}
                  </div>
                  {(record.locationNote || record.note) && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {record.locationNote || record.note}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No records for this species.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
